<article #cardRoot
    class="flex w-full flex-col overflow-hidden rounded-xl border border-border bg-card"
    [class.ring-2]="approved() || declined() || feedbackOnly()"
    [class.ring-green-300]="approved()"
    [class.ring-rose-300]="declined()"
    [class.ring-sky-300]="feedbackOnly()"
    [attr.aria-label]="displayName() + ', creator review'">
    <div class="flex flex-col gap-5 p-5">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-stretch">

            <div class="flex min-w-0 flex-col gap-5 lg:col-span-5">
                <div class="flex min-w-0 gap-4">
                    <button type="button" (click)="launchPortfolio()"
                        class="relative w-28 shrink-0 rounded-xl bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        [attr.aria-label]="'Open profile for ' + displayName()">
                        <div class="relative aspect-square w-full overflow-visible rounded-xl bg-muted">
                            <atom-ldg-img class="absolute top-0 left-0 size-full" [src]="photoSrc()"
                                inputClass="size-full object-cover rounded-xl" h="full" w="full"
                                aspect="aspect-square" fit="cover" radius="md"></atom-ldg-img>
                            @if (creator().isVerified) {
                            <div
                                class="absolute -bottom-1.5 -right-1.5 flex size-7 items-center justify-center rounded-full bg-primary">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                    stroke-linejoin="round" class="size-4 text-white" aria-hidden="true">
                                    <path d="M20 6 9 17l-5-5" />
                                </svg>
                            </div>
                            }
                        </div>
                    </button>

                    <div class="flex min-w-0 flex-1 flex-col gap-2">
                        <h3 class="m-0 text-base font-semibold text-primary">{{ displayName() }}</h3>
                        <div class="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-3.5 shrink-0 text-secondary" aria-hidden="true">
                                <path
                                    d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                            <p class="m-0 text-sm text-secondary">{{ hometown() }}</p>
                            <svg viewBox="0 0 6 6" aria-hidden="true" class="size-1.5 fill-secondary">
                                <circle r="3" cx="3" cy="3" />
                            </svg>
                            <p class="m-0 text-sm text-secondary">{{ creatorAge() }} years</p>
                        </div>
                        <p class="m-0 line-clamp-3 w-full text-sm text-secondary"
                            [class.italic]="!hasRealBio()">{{ displayBio() }}</p>
                        @if (interestLabels().length > 0) {
                        <div class="flex flex-row flex-wrap items-center gap-2">
                            @for (interestLabel of interestLabels(); track interestLabel) {
                            <span
                                class="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{{
                                interestLabel }}</span>
                            }
                        </div>
                        }
                    </div>
                </div>

                <dl class="grid grid-cols-2 gap-3" [class.sm:grid-cols-4]="hasAvgStats()">
                    <div
                        class="flex items-center gap-3 overflow-hidden rounded-lg border border-border bg-muted/60 px-4 py-3">
                        <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="currentColor" class="size-4 text-amber-400" aria-hidden="true">
                                <path
                                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                        </div>
                        <div class="min-w-0">
                            <dt class="truncate text-xs font-medium uppercase tracking-wide text-secondary">Rating</dt>
                            @if (isUnrated()) {
                            <dd class="m-0 text-base font-semibold tracking-tight text-primary">Unrated</dd>
                            } @else {
                            <dd class="m-0 text-base font-semibold tracking-tight text-primary">
                                {{ ratingValue() | number: "1.1" }}
                                @if (creator().reviews_summary?.total_reviews) {
                                ({{ creator().reviews_summary?.total_reviews }})
                                }
                            </dd>
                            }
                        </div>
                    </div>

                    <div
                        class="flex items-center gap-3 overflow-hidden rounded-lg border border-border bg-muted/60 px-4 py-3">
                        <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <div class="min-w-0">
                            <dt class="truncate text-xs font-medium uppercase tracking-wide text-secondary">Followers
                            </dt>
                            <dd class="m-0 text-base font-semibold tracking-tight text-primary">
                                {{ totalFollowersLabel() }}</dd>
                        </div>
                    </div>

                    @if (hasAvgStats()) {
                    <div
                        class="flex items-center gap-3 overflow-hidden rounded-lg border border-border bg-muted/60 px-4 py-3">
                        <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
                                <path
                                    d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </div>
                        <div class="min-w-0">
                            <dt class="truncate text-xs font-medium uppercase tracking-wide text-secondary">Avg. Views
                            </dt>
                            <dd class="m-0 text-base font-semibold tracking-tight text-primary">{{ avgViewsLabel() }}
                            </dd>
                        </div>
                    </div>

                    <div
                        class="flex items-center gap-3 overflow-hidden rounded-lg border border-border bg-muted/60 px-4 py-3">
                        <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" class="size-4 text-primary" aria-hidden="true">
                                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                                <polyline points="16 7 22 7 22 13" />
                            </svg>
                        </div>
                        <div class="min-w-0">
                            <dt class="truncate text-xs font-medium uppercase tracking-wide text-secondary">Avg.
                                Engagement</dt>
                            <dd class="m-0 text-base font-semibold tracking-tight text-primary">{{ avgEngagementLabel()
                                }}%</dd>
                        </div>
                    </div>
                    }
                </dl>

                <div class="flex flex-row flex-wrap gap-2">
                    <div class="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-2 text-sm font-medium text-muted-foreground"
                        [attr.aria-label]="'TikTok followers ' + tiktokFollowersLabel()">
                        <svg viewBox="0 0 24 24" fill="currentColor" class="size-4 shrink-0 text-primary"
                            aria-hidden="true">
                            <path
                                d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                        </svg>
                        <span>{{ tiktokFollowersLabel() }}</span>
                    </div>
                    <div class="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-2 text-sm font-medium text-muted-foreground"
                        [attr.aria-label]="'Instagram followers ' + instaFollowersLabel()">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="size-4 shrink-0 text-primary" aria-hidden="true">
                            <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                            <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                        </svg>
                        <span>{{ instaFollowersLabel() }}</span>
                    </div>
                    <div class="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-2 text-sm font-medium text-muted-foreground"
                        [attr.aria-label]="'Snapchat followers ' + snapFollowersLabel()">
                        <svg viewBox="0 0 24 24" fill="currentColor" class="size-4 shrink-0 text-primary"
                            aria-hidden="true">
                            <path
                                d="M21.9756 17.1497C21.8884 16.8566 21.4712 16.6499 21.4712 16.6499C21.4317 16.6288 21.3965 16.6099 21.3674 16.5951C20.672 16.2535 20.0555 15.8423 19.5365 15.3762C19.1193 15.0008 18.7623 14.5875 18.4758 14.1489C18.125 13.6132 17.961 13.1662 17.8904 12.9237C17.851 12.7655 17.8572 12.7022 17.8904 12.62C17.9174 12.5504 17.9984 12.485 18.0357 12.4534C18.2703 12.2847 18.6481 12.0358 18.8806 11.884C19.0819 11.7512 19.2542 11.6373 19.3559 11.5656C19.6818 11.3336 19.906 11.0974 20.0368 10.8423C20.207 10.5133 20.2278 10.1506 20.0949 9.79418C19.9164 9.31337 19.4743 9.02657 18.9117 9.02657C18.7872 9.02657 18.6585 9.04133 18.5298 9.06875C18.208 9.14045 17.9029 9.25643 17.6475 9.35765C17.6289 9.36609 17.6081 9.35133 17.6102 9.33024C17.6372 8.68705 17.6683 7.82244 17.5977 7.00211C17.5355 6.25981 17.3839 5.63349 17.139 5.0873C16.892 4.53901 16.5702 4.13412 16.319 3.841C16.0803 3.56263 15.6589 3.15141 15.0237 2.78237C14.1311 2.2636 13.114 2 12.0013 2C10.8908 2 9.87572 2.2636 8.98105 2.78237C8.30849 3.1725 7.87879 3.61324 7.68367 3.841C7.4325 4.13412 7.11075 4.53901 6.86372 5.0873C6.6167 5.63349 6.46724 6.2577 6.40497 7.00211C6.33439 7.82666 6.36345 8.62168 6.39252 9.33024C6.39252 9.35133 6.37383 9.36609 6.35308 9.35765C6.09775 9.25643 5.79261 9.14045 5.47086 9.06875C5.34423 9.04133 5.21553 9.02657 5.08891 9.02657C4.52844 9.02657 4.08629 9.31337 3.9057 9.79418C3.77285 10.1506 3.7936 10.5133 3.96382 10.8423C4.09667 11.0974 4.31878 11.3336 4.64469 11.5656C4.74432 11.6373 4.91869 11.7512 5.12005 11.884C5.34631 12.0337 5.71373 12.2763 5.95037 12.4429C5.97943 12.4639 6.07907 12.5399 6.11021 12.62C6.14342 12.7043 6.14965 12.7676 6.10605 12.9363C6.0334 13.1809 5.86941 13.6238 5.52483 14.1489C5.23837 14.5896 4.88133 15.0008 4.46409 15.3762C3.94514 15.8423 3.32862 16.2535 2.63323 16.5951C2.60002 16.612 2.56057 16.631 2.51906 16.6563C2.51906 16.6563 2.1039 16.8714 2.02502 17.1497C1.90877 17.5609 2.21807 17.9469 2.53151 18.1535C3.04631 18.4909 3.67321 18.6723 4.03647 18.7714C4.13819 18.7988 4.22952 18.8241 4.31256 18.8494C4.36445 18.8663 4.49523 18.9169 4.55127 18.9907C4.62185 19.0835 4.63016 19.1974 4.65506 19.326C4.69451 19.5432 4.78377 19.811 5.04739 19.9966C5.33801 20.1991 5.70542 20.2138 6.17248 20.2328C6.6603 20.2518 7.26643 20.275 7.96183 20.5091C8.28358 20.6166 8.57627 20.8001 8.91255 21.011C9.61832 21.4517 10.4964 22 11.9951 22C13.4959 22 14.3802 21.4496 15.0901 21.0067C15.4264 20.798 15.715 20.6166 16.0305 20.5112C16.7259 20.2771 17.332 20.2539 17.8198 20.2349C18.2869 20.2159 18.6543 20.2033 18.9449 19.9987C19.2272 19.8005 19.3082 19.5053 19.3456 19.2838C19.3663 19.1742 19.3788 19.0751 19.441 18.9949C19.495 18.9253 19.6154 18.8768 19.6715 18.8579C19.7566 18.8305 19.8521 18.8051 19.9579 18.7756C20.3212 18.6765 20.7779 18.5605 21.3321 18.2421C22.0005 17.8562 22.0462 17.3838 21.9756 17.1497Z" />
                        </svg>
                        <span>{{ snapFollowersLabel() }}</span>
                    </div>
                    <div class="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-2 text-sm font-medium text-muted-foreground"
                        [attr.aria-label]="'YouTube subscribers ' + youtubeFollowersLabel()">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="size-4 shrink-0 text-primary" aria-hidden="true">
                            <path
                                d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                            <path d="m10 15 5-3-5-3z" />
                        </svg>
                        <span>{{ youtubeFollowersLabel() }}</span>
                    </div>
                </div>
            </div>

            <div class="flex min-w-0 flex-col gap-2 lg:col-span-4">
                <h4 class="m-0 flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-wide text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-3.5 text-secondary" aria-hidden="true">
                        <path d="M2 7v10" />
                        <path d="M6 5v14" />
                        <rect width="12" height="18" x="10" y="3" rx="2" />
                    </svg>
                    Portfolio videos
                </h4>
                <div class="grid grid-cols-3 gap-2">
                    @if (!isInView() || portfolioLoading()) {
                    @for (placeholder of portfolioPlaceholders; track placeholder) {
                    <div class="aspect-9/16 w-full rounded-lg bg-muted" aria-hidden="true"></div>
                    }
                    } @else if (portfolioPreviewVideos().length > 0) {
                    @for (video of portfolioPreviewVideos(); track video.id) {
                    <button type="button" (click)="previewVideo(video)"
                        class="aspect-9/16 w-full overflow-hidden rounded-lg hover:ring-2 hover:ring-primary/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        [attr.aria-label]="'Play portfolio video for ' + displayName()">
                        @if (hasPortfolioThumbnail(video)) {
                        <img [src]="video.thumbnailURL" alt="" class="size-full object-cover" />
                        } @else {
                        <atom-video-thumbnail [videoUrl]="video.fullUrl"
                            [thumbnailURL]="video.thumbnailURL ?? undefined" [videoFormat]="videoFormat"
                            [thumb_cdn_size]="'gallery'"></atom-video-thumbnail>
                        }
                    </button>
                    }
                    } @else {
                    <p class="col-span-3 m-0 text-sm text-secondary">No portfolio videos yet.</p>
                    }
                </div>
            </div>

            <div class="flex min-w-0 flex-col gap-2 lg:col-span-3">
                <h4 class="m-0 text-xs font-semibold uppercase tracking-wide text-secondary">Your review</h4>
                @if (invited()) {
                <p class="m-0 flex items-center gap-1 text-sm font-medium text-violet-700 dark:text-violet-300">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 text-violet-500 dark:text-violet-300" aria-hidden="true">
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    <span>Invited</span>
                </p>
                }
                @if (reviewLabel()) {
                <p class="m-0 flex items-center gap-1 text-sm font-medium" [class]="reviewLabelTextClass()">
                    @if (declined()) {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 text-rose-400 dark:text-rose-300" aria-hidden="true">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                    } @else if (approved()) {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 text-green-500 dark:text-green-300" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                    } @else if (feedbackOnly()) {
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4 shrink-0 text-sky-500 dark:text-sky-300" aria-hidden="true">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    }
                    <span>{{ reviewLabel() }}</span>
                </p>
                }

                <button type="button" class="secondary-button inline-flex w-full items-center justify-center gap-2"
                    (click)="approve.emit()" [attr.aria-pressed]="approveSelected()"
                    [disabled]="approveSelected()">
                    @if (approveSelected()) {
                    <span class="size-1.5 rounded-full bg-green-400" aria-hidden="true"></span>
                    Approved
                    } @else if (inviteOnApprove()) {
                    Approve & invite
                    } @else {
                    Approve
                    }
                </button>
                <button type="button" class="secondary-button inline-flex w-full items-center justify-center gap-2"
                    (click)="decline.emit()" [attr.aria-pressed]="declined()" [disabled]="declined()">
                    @if (declined()) {
                    <span class="size-1.5 rounded-full bg-rose-400" aria-hidden="true"></span>
                    Declined
                    } @else {
                    Decline
                    }
                </button>
                <button type="button" class="secondary-button inline-flex w-full items-center justify-center gap-2"
                    (click)="toggleFeedback()" [attr.aria-pressed]="feedbackOnly()"
                    [attr.aria-expanded]="feedbackOpen()" [attr.aria-controls]="noteFieldId()">
                    @if (feedbackOnly()) {
                    <span class="size-1.5 rounded-full bg-sky-400" aria-hidden="true"></span>
                    }
                    @if (reviewNote()) {
                    Edit feedback
                    } @else {
                    Leave feedback
                    }
                </button>

                @if (feedbackOpen()) {
                <label class="sr-only" [attr.for]="noteFieldId()">Feedback for {{ displayName() }}</label>
                <textarea [id]="noteFieldId()" rows="3" [value]="draftFeedback()" [attr.maxlength]="noteMaxLength"
                    (input)="onDraftFeedbackInput($event)"
                    class="w-full resize-none rounded-md border border-border bg-card px-3 py-2 text-sm text-primary"
                    placeholder="What should they know?"></textarea>
                <div class="flex flex-col gap-2">
                    <button type="button" class="secondary-button w-full" (click)="submitFeedbackApprove()">
                        @if (inviteOnApprove()) {
                        Approve & invite with feedback
                        } @else {
                        Approve with feedback
                        }
                    </button>
                    <button type="button" class="secondary-button w-full" (click)="submitFeedbackDecline()">Decline with
                        feedback</button>
                    <button type="button" class="secondary-button w-full" (click)="submitFeedbackOnly()"
                        [disabled]="!hasDraftFeedback()">Just
                        feedback</button>
                </div>
                } @else if (reviewNote()) {
                <p class="m-0 text-sm text-secondary">{{ reviewNote() }}</p>
                }
            </div>
        </div>
    </div>
</article>
